// Revisado: 2026-02-13 [I18n Scan]
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useSocket from '../../../hooks/useSocket';
import useGameRoom from './useGameRoom';
import RoomCard from './RoomCard';

/**
 * GameLobby - Componente compartido para gestión de salas (Layer 2)
 * Maneja la visualización de salas, creación y unión, delegando la lógica a useGameRoom.
 * 
 * @param {string} gameId - Identificador del juego (ej: "guess-who")
 * @param {Component} GameBoardComponent - Componente específico del juego (Layer 3)
 */
const GameLobby = ({ gameId, GameBoardComponent }) => {
    const { t } = useTranslation();
    // Obtenemos socket para pasarlo al componente hijo
    const { socket } = useSocket();

    // Lógica de salas encapsulada en el hook
    const {
        rooms,
        currentRoom,
        createRoom,
        joinRoom,
        leaveRoom,
        loading,
        error,
        connected
    } = useGameRoom(gameId);

    // Si hay error de conexión
    if (error) {
        return (
            <div className="text-center p-10 bg-gray-800/50 rounded-xl border border-red-500/30">
                <p className="text-red-400 text-xl mb-4 font-bold">❌ Error</p>
                <p className="text-gray-300 mb-6">{error}</p>
                <button
                    onClick={() => window.location.reload()}
                    className="bg-teal-600 hover:bg-teal-500 text-white px-6 py-2 rounded-lg transition-colors font-bold shadow-lg"
                >
                    Retry Connection
                </button>
            </div>
        );
    }

    // Si no está conectado
    if (!connected) {
        return (
            <div className="text-center p-20">
                <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-teal-500 mx-auto mb-4"></div>
                <p className="text-xl text-teal-400 font-light tracking-wide">Connecting to game server...</p>
            </div>
        );
    }

    // MODO JUEGO: Si estás en una sala, renderiza el tablero específico
    if (currentRoom) {
        return (
            <div className="game-container animate-fade-in">
                <GameBoardComponent
                    socket={socket}
                    roomId={currentRoom}
                    onLeave={leaveRoom}
                />
            </div>
        );
    }

    // MODO LOBBY: Lista de salas
    return (
        <div className="max-w-5xl mx-auto p-6 animate-fade-in">
            <div className="mb-4">
                <Link to="/games" className="text-gray-400 hover:text-white flex items-center gap-2 transition-colors w-fit">
                    ← {t('GameLobby.back_hub', 'Back to Hub')}
                </Link>
            </div>

            <header className="flex flex-col md:flex-row justify-between items-center mb-10 gap-4">
                <div>
                    <h2 className="text-4xl font-black text-white tracking-tighter drop-shadow-md">
                        {t('GameLobby.title', 'GAME LOBBY')}
                    </h2>
                    <p className="text-teal-400 font-medium uppercase tracking-widest text-sm">
                        {gameId.replace(/-/g, ' ')}
                    </p>
                </div>

                <button
                    onClick={createRoom}
                    disabled={loading}
                    className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-white px-8 py-3 rounded-full font-bold shadow-lg hover:shadow-teal-500/20 transition-all transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                    {loading ? (
                        <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
                    ) : (
                        <span>{t('GameLobby.btn_create', '🎮 Create New Room')}</span>
                    )}
                </button>
            </header>

            {/* Grid de Salas */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {rooms.length > 0 ? (
                    rooms.map(room => (
                        <RoomCard
                            key={room.room_id}
                            room={room}
                            onJoin={() => joinRoom(room.room_id)}
                        />
                    ))
                ) : (
                    <div className="col-span-full py-16 text-center bg-gray-800/30 rounded-2xl border-2 border-dashed border-gray-700">
                        <div className="text-6xl mb-4 opacity-50">🕹️</div>
                        <h3 className="text-xl text-white font-bold mb-2">
                            {t('GameLobby.no_rooms_title', 'No active rooms found')}
                        </h3>
                        <p className="text-gray-400 max-w-md mx-auto">
                            {t('GameLobby.no_rooms_desc', 'Be the first one to start a game! Click the "Create New Room" button above.')}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GameLobby;
